import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { from, Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HTML } from '~/core/helpers/html.helper';
import { WindowService } from '~/core/services/window/window.service';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { GeoCoordinates } from './geocoordinates.interface';
declare global {
  interface Window {
    woosmap: {
      map: {
        LocalitiesService: () => void;
      };
      multisearch: (config: {}) => {
        autocompleteMulti: (suggestion: string) => {};
      };
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class MapsService {
  private readonly apiKey = environment.applications.woosmap.apiKey;

  private readonly localitiesUrl = 'https://api.woosmap.com/localities';
  private readonly sdkUrl = 'https://sdk.woosmap.com';

  private readonly predictionsApiUrl = `${this.localitiesUrl}/autocomplete`;
  private readonly geocodeApiUrl = `${this.localitiesUrl}/geocode`;

  private readonly mapApiUrl = `${this.sdkUrl}/map/map.js?key=${this.apiKey}&callback=initMap`;
  private readonly multisearchApiUrl = `${this.sdkUrl}/multisearch/multisearch.js?version=6`;

  private readonly isMapScriptInitialized = new BehaviorSubject<boolean>(false);
  private readonly isMultisearchScriptInitialized =
    new BehaviorSubject<boolean>(false);

  public map: woosmap.map.Map;
  public marker: woosmap.map.Marker;
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  public multisearch: any;

  // GMAPS VAR
  private readonly gMapsBaseUrl = '/api/maps';
  public googleMapsScriptLoaded = false;
  public isInitializedSubject = new BehaviorSubject<boolean>(false);
  public isGmapInitializedSubject = new BehaviorSubject<boolean>(false);
  public googleMapCoordinates: google.maps.LatLng;

  constructor(
    private readonly httpClient: HttpClient,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly windowService: WindowService,
    private readonly translateService: TranslateService
  ) {}

  public createScript(scriptId: string, scriptUrl: string): Observable<void> {
    if (this.document.getElementById(scriptId)) {
      scriptId === 'woosmap-script'
        ? this.isMapScriptInitialized.next(true)
        : this.isMultisearchScriptInitialized.next(true);
      return;
    }

    const script = this.document.createElement('script');
    script.id = scriptId;
    script.src = scriptUrl;
    script.defer = true;
    script.onload = () => {
      scriptId === 'woosmap-script'
        ? this.isMapScriptInitialized.next(true)
        : this.isMultisearchScriptInitialized.next(true);
    };

    HTML.unshift(this.document.head, script);
    this.isInitializedSubject.next(true);
  }

  public loadScript(): void {
    const id = 'google-maps-script';
    if (this.document.getElementById(id)) {
      this.googleMapsScriptLoaded = true;
      return;
    }

    const apiKey = environment.applications.woosmap.gmapApiKey;
    const script = this.document.createElement('script');
    script.id = id;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=mapsCallback`;
    script.async = true;
    script.onload = () => {
      this.isGmapInitializedSubject.next(true);
      this.googleMapsScriptLoaded = true;
    };

    HTML.unshift(this.document.head, script);
  }

  public isGmapInitialized(): Observable<boolean> {
    return this.isGmapInitializedSubject.asObservable();
  }

  public initMapScript(): void {
    this.createScript('woosmap-script', this.mapApiUrl);
  }

  public initMultisearchScript(): void {
    this.createScript('woosmap-script-multisearch', this.multisearchApiUrl);
  }

  public initMap(lat: number, lng: number): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      const mapElement = document.getElementById('map');
      if (mapElement) {
        this.mapScriptInitialized().subscribe((woosmapLoaded) => {
          if (
            woosmapLoaded === true &&
            window.woosmap?.map?.Map &&
            mapElement
          ) {
            this.map = new window.woosmap.map.Map(mapElement, {
              center: { lat, lng },
              zoom: 16,
            });
            this.initMarker();
          }
        });
      }
    }
  }

  public initMarker(): void {
    this.marker = new window.woosmap.map.Marker({
      position: this.map.getCenter(),
      icon: {
        url: 'assets/maps/dot-marker.png',
        scaledSize: {
          height: 64,
          width: 46,
        },
      },
    });
    this.marker.setMap(this.map);
  }

  public getPlace(
    id: string
  ): Observable<woosmap.map.localities.LocalitiesDetailsResult | null> {
    if (isPlatformServer(this.platformId)) {
      return of(null);
    }

    const detailsRequest = {
      publicId: id,
      language: this.translateService.currentLang.split('-')[1],
    };

    if (this.windowService.nativeWindow.woosmap) {
      const localitiesService =
        new this.windowService.nativeWindow.woosmap.map.LocalitiesService();
      return from(localitiesService.getDetails(detailsRequest)).pipe(
        map((response: woosmap.map.localities.LocalitiesDetailsResponse) => {
          return response['result'];
        }),
        catchError(() => {
          return of(null);
        })
      );
    }

    return of(null);
  }

  public getPredictions(
    input: string,
    components: string, // country:gb|country:fr OR country:fr
    limit: number,
    types: string
  ): Observable<woosmap.map.localities.LocalitiesPredictions[]> {
    return this.httpClient
      .get<woosmap.map.localities.LocalitiesAutocompleteResponse>(
        this.predictionsApiUrl,
        {
          params: {
            key: this.apiKey,
            input,
            components,
            limit,
            types,
          },
        }
      )
      .pipe(
        map((result: woosmap.map.localities.LocalitiesAutocompleteResponse) => {
          return result.localities;
        })
      );
  }

  public mapScriptInitialized(): Observable<boolean> {
    return this.isMapScriptInitialized.asObservable();
  }

  public reverseGeocode(
    lat: number,
    lng: number
  ): Observable<woosmap.map.localities.LocalitiesGeocodeResult> {
    return this.httpClient
      .get<{ results: woosmap.map.localities.LocalitiesGeocodeResult }>(
        this.geocodeApiUrl,
        {
          params: {
            key: this.apiKey,
            latlng: `${lat}, ${lng}`,
          },
        }
      )
      .pipe(map((response) => response.results?.[0]));
  }

  // GMAPS METHODS
  public getGMapsGeoCoordinates(address: string): Observable<GeoCoordinates> {
    return this.httpClient
      .get(
        `${environment.applications.front.url}${this.gMapsBaseUrl}/geocode`,
        {
          params: {
            address,
          },
        }
      )
      .pipe(
        catchError((error) => {
          return error;
        }),
        map(
          (result: {
            results: {
              address_components;
              formatted_address;
              geometry;
              place_id;
            }[];
            status: string;
          }) => {
            const { results, status } = result;

            if (status === 'ZERO_RESULTS') {
              return { address: '', latitude: 0, longitude: 0, place_id: '' };
            }

            const element = results[0];
            const city = element?.address_components?.find((component) =>
              component.types.includes('locality')
            );
            const country = element?.address_components?.find((component) =>
              component.types.includes('country')
            );

            return {
              address: `${city?.long_name}, ${country?.long_name}`,
              latitude: element?.geometry?.location?.lat,
              longitude: element?.geometry?.location?.lng,
              placeId: element?.place_id,
            };
          }
        )
      );
  }
}
